import { can, type MeDto, type Permission, type Role } from '@af/shared';
import { createContext, type ReactNode, useContext } from 'react';

const RoleContext = createContext<Role | null>(null);

export function RoleProvider({ role, children }: { role: Role | null; children: ReactNode }) {
  return <RoleContext.Provider value={role}>{children}</RoleContext.Provider>;
}

export function useRole(): Role | null {
  return useContext(RoleContext);
}

export function useCan(perm: Permission): boolean {
  return can(useContext(RoleContext), perm);
}

/** Renders children only when the current role has `perm` (UI convenience — the API enforces). */
export function Can({
  perm,
  children,
  fallback = null,
}: {
  perm: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return <>{useCan(perm) ? children : fallback}</>;
}

/**
 * May add a company: a platform admin always; with billing, the owner of the active company's
 * subscription while the plan has room (the API checks the same under a lock).
 */
export function canCreateCompany(me: Pick<MeDto, 'platformAdmin' | 'billing'> | undefined): boolean {
  if (me?.platformAdmin) return true;
  const b = me?.billing;
  if (!b?.enabled || !b.canManage) return false;
  return b.limits === null || b.limits.companies === null || b.companiesUsed < b.limits.companies;
}
